import { useEffect, useRef } from 'react';

export interface UseAutoScrollOptions {
  /**
   * Scroll behavior type
   * @default 'smooth'
   */
  behavior?: ScrollBehavior;
  /**
   * Whether to enable auto-scrolling
   * @default true
   */
  enabled?: boolean;
}

export interface UseAutoScrollReturn {
  /** Ref to attach to the end of the scrollable content */
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  /** Ref to attach to the scrollable container */
  messagesContainerRef: React.RefObject<HTMLDivElement | null>;
  /** Manually scroll to the bottom */
  scrollToBottom: () => void;
  /** Manually scroll to the top */
  scrollToTop: () => void;
}

/**
 * Hook to manage auto-scrolling behavior for chat message containers
 *
 * @param dependencies - Array of dependencies to trigger auto-scroll (e.g., messages array)
 * @param options - Configuration options
 * @returns Refs and scroll control functions
 *
 * @example
 * ```tsx
 * const { messagesEndRef, messagesContainerRef, scrollToBottom } = useAutoScroll([messages]);
 *
 * return (
 *   <div ref={messagesContainerRef}>
 *     {messages.map(msg => <Message key={msg.id} {...msg} />)}
 *     <div ref={messagesEndRef} />
 *   </div>
 * );
 * ```
 */
export const useAutoScroll = (
  dependencies: React.DependencyList,
  options: UseAutoScrollOptions = {},
): UseAutoScrollReturn => {
  const { behavior = 'smooth', enabled = true } = options;

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when dependencies change
  useEffect(() => {
    if (enabled) {
      messagesEndRef.current?.scrollIntoView({ behavior });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, enabled, behavior]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  const scrollToTop = () => {
    messagesContainerRef.current?.scrollTo({
      top: 0,
      behavior,
    });
  };

  return {
    messagesEndRef,
    messagesContainerRef,
    scrollToBottom,
    scrollToTop,
  };
};
